import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    CircularProgress,
    Alert,
    Box,
    Checkbox,
    IconButton,
    Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ErrorBoundary from './ErrorBoundary';
import { fetchAllImages, uploadImage, deleteImage } from '../api/archivesApi';

// Remove /api from the base URL for static assets
const STATIC_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');

const Archives = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImages, setSelectedImages] = useState(new Set());
    const [uploading, setUploading] = useState(false);

    const fetchImages = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchAllImages();
            setImages(data);
        } catch (err) {
            setError('Failed to fetch images');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteImage(id);
            setImages((prevImages) => prevImages.filter((image) => image.id !== id));
            setSelectedImages((prev) => {
                const newSelected = new Set(prev);
                newSelected.delete(id);
                return newSelected;
            });
        } catch (err) {
            console.error(`Failed to delete image with ID ${id}:`, err);
            setError(`Failed to delete image with ID ${id}`);
        }
    };

    const handleImageSelect = (id) => {
        setSelectedImages((prev) => {
            const newSelected = new Set(prev);
            if (newSelected.has(id)) {
                newSelected.delete(id);
            } else {
                newSelected.add(id);
            }
            return newSelected;
        });
    };

    const handleFileUpload = async (event) => {
        const files = event.target.files;
        if (!files) return;

        try {
            setUploading(true);
            setError(null);
            
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const description = `Uploaded ${new Date().toLocaleDateString()}`;
                await uploadImage(file, description);
            }
            
            await fetchImages(); // Refresh the list after upload
        } catch (err) {
            setError('Failed to upload images');
            console.error('Error:', err);
        } finally {
            setUploading(false);
        }
    };

    const getImageUrl = (filepath) => {
        return `${STATIC_BASE_URL}${filepath}`;
    };

    useEffect(() => {
        fetchImages();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" component="h1">
                    Image Archives
                </Typography>
                <Box>
                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        id="image-upload"
                        onChange={handleFileUpload}
                        disabled={uploading}
                    />
                    <label htmlFor="image-upload">
                        <Button
                            variant="contained"
                            color="primary"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            disabled={uploading}
                        >
                            {uploading ? 'Uploading...' : 'Upload Images'}
                        </Button>
                    </label>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {images.length === 0 ? (
                <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
                    <CardContent>
                        <Box textAlign="center" py={4}>
                            <Typography variant="h5" color="textSecondary" gutterBottom>
                                No Images Found
                            </Typography>
                            <Typography variant="body1" color="textSecondary" paragraph>
                                Your image archive is currently empty. Click the "Upload Images" button to add your first images.
                            </Typography>
                            <label htmlFor="image-upload">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component="span"
                                    startIcon={<CloudUploadIcon />}
                                    sx={{ mt: 2 }}
                                    disabled={uploading}
                                >
                                    {uploading ? 'Uploading...' : 'Upload Your First Images'}
                                </Button>
                            </label>
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {images.map((image) => (
                        <Grid item xs={12} sm={6} md={4} key={image.id}>
                            <ErrorBoundary>
                                <Card 
                                    sx={{ 
                                        position: 'relative',
                                        '&:hover': {
                                            boxShadow: 6
                                        }
                                    }}
                                >
                                    <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                                        <Checkbox
                                            checked={selectedImages.has(image.id)}
                                            onChange={() => handleImageSelect(image.id)}
                                            sx={{
                                                color: 'white',
                                                '&.Mui-checked': {
                                                    color: 'primary.main',
                                                },
                                            }}
                                        />
                                    </Box>
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={getImageUrl(image.filepath)}
                                        alt={image.filename}
                                        sx={{ 
                                            objectFit: 'cover',
                                            '&:hover': {
                                                cursor: 'pointer'
                                            }
                                        }}
                                        onClick={() => window.open(getImageUrl(image.filepath), '_blank')}
                                    />
                                    <CardContent>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="h6" component="div" noWrap>
                                                {image.filename}
                                            </Typography>
                                            <Tooltip title="Delete">
                                                <IconButton 
                                                    onClick={() => handleDelete(image.id)}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {image.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </ErrorBoundary>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Container>
    );
};

export default Archives; 